import { Injectable, computed, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';

export enum ActivePage {
  ProjectDashboard = 'projectDashboard',
  ProjectSubsection = 'projectSubsection',
  CampaignOverview = 'campaignOverview',
  WorkspaceDashboard = 'workspaceDashboard',
  MySpace = 'mySpace',
}

export interface CommentPageStateModel {
  [key: string]: boolean;
}

export interface AppStateModel {
  activePage: string;
  enableCommentPage: boolean;
  enableTaskPage: boolean;
  commentPage: CommentPageStateModel;
  commentPanelFullSize: boolean;
  commentPanelWidth: number;
  commentPluginReady: boolean;
  navigatingToProjectDashboard: boolean;
}

@Injectable({ providedIn: 'root' })
export class AppStateService {
  private readonly state = signal<AppStateModel>({
    activePage: '',
    enableCommentPage: true,
    enableTaskPage: true,
    commentPage: {},
    commentPanelFullSize: false,
    commentPanelWidth: 366,
    commentPluginReady: false,
    navigatingToProjectDashboard: false,
  });

  private readonly documentRef = inject(DOCUMENT);
  private hasInitializedPlatformCommentsChatExpandedState = false;
  private platformCommentsChatExpandedState: boolean = true;

  setState(next: AppStateModel): void {
    this.state.set({ ...next });
  }

  patchState(partial: Partial<AppStateModel>): void {
    const current = this.state();
    this.state.set({ ...current, ...partial });
  }

  reset(): void {
    this.state.set({
      activePage: '',
      enableCommentPage: true,
      enableTaskPage: true,
      commentPage: {},
      commentPanelFullSize: false,
      commentPanelWidth: 366,
      commentPluginReady: false,
      navigatingToProjectDashboard: false,
    });
  }

  setCommentPanelFullSize(isFullSize: boolean): void {
    this.patchState({ commentPanelFullSize: isFullSize });
  }

  getCommentPanelFullSize(): boolean {
    return this.state().commentPanelFullSize;
  }

  commentPanelFullSize = computed(() => this.state().commentPanelFullSize);

  setCommentPageCommentExpanded(key: string, isExpanded: boolean): void {
    this.platformCommentsChatExpandedState = isExpanded;
    const current = this.state();
    this.state.set({
      ...current,
      enableCommentPage: true,
      enableTaskPage: true,
      commentPage: {
        ...current.commentPage,
        [key]: isExpanded,
      },
    });
    const body = this.documentRef?.body;
    if (!body) {
      return;
    }
    if (this.state().enableCommentPage) {
      if (isExpanded) {
        body.classList.add('comments-chat-expanded');
      } else {
        body.classList.remove('comments-chat-expanded');
      }
    }
  }

  ensureCommentPageExpandedOnFirstLoad(key: string): void {
    if (this.hasInitializedPlatformCommentsChatExpandedState) {
      return;
    }
    if (!key) {
      return;
    }

    const current = this.state();
    if (typeof current.commentPage?.[key] === 'boolean') {
      this.hasInitializedPlatformCommentsChatExpandedState = true;
      return;
    }

    this.hasInitializedPlatformCommentsChatExpandedState = true;
    this.setCommentPageCommentExpanded(key, true);
  }

  setActivePage(page: string): void {
    const current = this.state();
    this.state.set({
      ...current,
      activePage: page,
    });

    if (page) {
      const desiredExpandedState = this.platformCommentsChatExpandedState;
      const existingExpandedState = this.state().commentPage?.[page];

      if (existingExpandedState !== desiredExpandedState) {
        this.setCommentPageCommentExpanded(page, desiredExpandedState);
      }
    }
  }

  getActivePage(): string {
    return this.state().activePage;
  }

  isEnableCommentPage(): boolean {
    return this.state().enableCommentPage;
  }

  isEnableTaskPage(): boolean {
    return this.state().enableTaskPage;
  }

  isCommentPageCommentExpanded(key: string): boolean {
    return this.state().commentPage[key] || false;
  }

  /** Per-page comment/task expanded flags, as a signal so callers can react to changes. */
  commentPageState = computed(() => this.state().commentPage);

  setCommentPanelWidth(width: number): void {
    this.patchState({ commentPanelWidth: width });
  }

  getCommentPanelWidth(): number {
    return this.state().commentPanelWidth;
  }

  commentPanelWidthSignal = computed(() => this.state().commentPanelWidth);

  setCommentPluginReady(isReady: boolean): void {
    this.patchState({ commentPluginReady: isReady });
  }

  getCommentPluginReady(): boolean {
    return this.state().commentPluginReady;
  }

  commentPluginReady = computed(() => this.state().commentPluginReady);

  setNavigatingToProjectDashboard(isNavigating: boolean): void {
    this.patchState({ navigatingToProjectDashboard: isNavigating });
  }

  isNavigatingToProjectDashboard(): boolean {
    return this.state().navigatingToProjectDashboard;
  }
}
